import { GetStaticProps, GetStaticPropsContext } from 'next';
import getConfig from 'next/config';
import { useEffect } from 'react';
import { ClearInNavbar } from '../../../components/Navbar/ClearNavbar';
import WebinarsDataService from '../../../components/Webinars/Services/webinars.service';
import {
  IDETAIL_PAGE_DATA,
  IWEBINAR_HOMEPAGE_DATA,
} from '../../../components/Webinars/Utils/types';
import { WebinarDetailPage } from '../../../components/Webinars/WebinarDetailPage';
import { Meta } from '../../../layout/Meta';
import { FM_ACTIONS, returnNoPaths } from '../../../utils/UtilFunctions';
import { handleTrack } from '../../../utils/tracker';
import { staticPropsWrapper } from '../../../utils/static-pages/utils';
import {
  DEPLOYMENT_DOMAIN,
  ENABLE_WEBINAR_PAGES,
} from '../../../env-vars.constants';
const { publicRuntimeConfig } = getConfig();

const IndividualWebinar = ({
  data,
}: {
  data: IDETAIL_PAGE_DATA;
}): JSX.Element => {
  useEffect(() => {
    if (window !== undefined) {
      handleTrack({
        label: window.location.pathname,
        action: FM_ACTIONS.pageview,
        categoryName: data.webinarDetails.product?.title,
      });
    }
  }, [data.webinarDetails.product?.title]);

  return (
    <div>
      <ClearInNavbar />
      <Meta
        title={data.webinarDetails.seo_title || data.webinarDetails.title}
        description={
          data.webinarDetails.meta_description ||
          data.webinarDetails.description
        }
        canonical={`${publicRuntimeConfig.CLEARTAX_DOMAIN}/${data.path}`}
        key="robots"
      />

      <WebinarDetailPage data={data} />
    </div>
  );
};

export default IndividualWebinar;

export const getStaticPaths = async () => {
  if (DEPLOYMENT_DOMAIN !== 'cleartax.in' || ENABLE_WEBINAR_PAGES !== 'true') {
    return returnNoPaths();
  }
  try {
    const { data: allWebinarsData } =
      await WebinarsDataService.getAllWebinarPaths();

    if (allWebinarsData.length === 0) {
      console.warn('No webinars found in API response');
      return {
        paths: [],
        fallback: false,
      };
    }

    const pathsArray = allWebinarsData.map(
      (webinar: IWEBINAR_HOMEPAGE_DATA) => {
        return {
          params: {
            individualWebinar: webinar.slug,
          },
        };
      }
    );

    return {
      paths: pathsArray,
      fallback: false,
    };
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('Error in getStaticPaths:', errorMessage);
    return {
      paths: [],
      fallback: false,
    };
  }
};

export const getStaticProps: GetStaticProps = staticPropsWrapper(
  __filename,
  async (context: GetStaticPropsContext) => {
    const { individualWebinar } = context.params!;

    try {
      const webinarResult = await WebinarsDataService.getWebinarBySlug(
        individualWebinar as string
      );
      return {
        props: {
          data: { webinarDetails: webinarResult.data },
        },
      };
    } catch (err: unknown) {
      console.log(
        'Error fetching webinar details for slug: ',
        individualWebinar,
        (err as Error)?.message
      );
      return { notFound: true };
    }
  }
);
